// Imports => React
import { useRef, useMemo, useEffect, memo } from "react";
import ReactCSSTransitionReplace from "react-css-transition-replace";
import clsx from "clsx";

// Imports => Constants
import { ICONS, THEMES } from "@opp/ui-kit";

// Imports => Utilities
import { AcUUID } from "@opp/ui-kit";

// Imports => Atoms
import { AcCard, AcIconButton } from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import AcCopyText from "@src/atoms/ac-copy-text/ac-copy-text";

const _CLASSES = {
  MAIN: "ac-modal",
  OPEN: "ac-modal--visible",
  CENTERED: "ac-modal--centered",
  CARD: "ac-modal__card",
  CARD_OVERFLOW_VISIBLE: "ac-modal__card--overflow-visible",
  OFFSET: "ac-modal__card--offset",
  TITLE: "ac-modal__title",
  COPY_UID: "ac-modal__uid",
  CONTENT: "ac-modal__content",
  ICON: {
    MAIN: "ac-icon",
    CLOSE: "ac-modal__close-icon",
  },
};

// Component
const AcModal = ({
  id = AcUUID(),
  title,
  uid,
  children,
  callback,
  background,
  visible = false,
  closeable = true,
  offset = false,
  centered = false,
  overflowVisible = false,
  width,
  height,
  t,
}) => {
  const $modal = useRef(null);

  useEffect(() => {
    if (closeable && visible) {
      addEvents();
    } else if (!closeable) {
      removeEvents();
    }

    return () => removeEvents();
  }, [visible, closeable]);

  const addEvents = () => {
    document.addEventListener("keyup", handleKeyUp, false);
  };

  const removeEvents = () => {
    document.removeEventListener("keyup", handleKeyUp, false);
  };

  const handleKeyUp = (event) => {
    if (!visible || !closeable) {
      return;
    }

    if (event && event.persist) {
      event.persist();
    }
    const key = event.key || event.which;

    if ($modal && $modal.current) {
      const inside = $modal.current.contains(event.target);

      if (inside) {
        return;
      }
    }

    if (key) {
      switch (key) {
        case "Escape":
        case 27:
          handleCloseCallback(event);
          break;

        default:
      }
    }
  };

  const handleBackdropClick = (event) => {
    if (!closeable) {
      return;
    }

    const $active_element = document.activeElement;
    const $inputs = ["input", "select", "button", "textarea"];

    if (
      $active_element &&
      $inputs.indexOf($active_element.tagName.toLowerCase()) > -1
    ) {
      return;
    }

    if (event.target && event.target.classList.contains(_CLASSES.MAIN)) {
      handleCloseCallback(event);
    }
  };

  const handleCloseCallback = (event) => {
    if (event && event.cancelable) {
      if (event && event.persist) {
        event.persist();
      }
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
    }
    if (callback) {
      callback(event);
    }
  };

  const getContentStyleClassNames = useMemo(() => {
    return clsx(_CLASSES.CONTENT);
  }, []);

  const getTitleStyleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE);
  }, []);

  const getCopyUidStyleClassNames = useMemo(() => {
    return clsx(_CLASSES.COPY_UID);
  }, []);

  const getCardStyleClassNames = useMemo(() => {
    return clsx(
      _CLASSES.CARD,
      offset && _CLASSES.OFFSET,
      overflowVisible && _CLASSES.CARD_OVERFLOW_VISIBLE
    );
  }, [offset, overflowVisible]);

  const getCloseIconClassNames = useMemo(() => {
    return clsx(_CLASSES.ICON.MAIN, _CLASSES.ICON.CLOSE);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      visible && _CLASSES.OPEN,
      centered && _CLASSES.CENTERED
    );
  }, [visible, centered]);

  return (
    <div
      className={getStyleClassNames}
      onClick={handleBackdropClick}
      style={{ background }}
    >
      <AcCard
        className={getCardStyleClassNames}
        width={width}
        height={height}
        ref={$modal}
        role={"dialog"}
        aria-modal={"true"}
        aria-labelledby={"ac-dialog-title"}
        aria-describedby={"ac-dialog-body"}
      >
        {title && (
          <div className={getTitleStyleClassNames} id={"ac-dialog-title"}>
            {title}
            {uid && (
              <AcCopyText
                className={getCopyUidStyleClassNames}
                t={t}
                value={uid}
                title={uid}
                id={"ac-tra-uid"}
                fixed
              >
                {uid}
              </AcCopyText>
            )}
          </div>
        )}
        {closeable && (
          <AcIconButton
            icon={ICONS.CLOSE}
            theme={THEMES.EGGWHITE}
            className={getCloseIconClassNames}
            callback={handleCloseCallback}
            title={t("modal.click-to-close", "Click to close modal", {
              ns: "common",
            })}
            aria-label={t("modal.click-to-close", "Click to close modal", {
              ns: "common",
            })}
          />
        )}
        {children && (
          <ReactCSSTransitionReplace
            transitionName="fade-wait"
            transitionEnterTimeout={250}
            transitionLeaveTimeout={250}
          >
            <div
              key={id}
              id={"ac-dialog-body"}
              className={getContentStyleClassNames}
            >
              {children}
            </div>
          </ReactCSSTransitionReplace>
        )}
      </AcCard>
    </div>
  );
};

export default withTranslation(["common"])(memo(AcModal));
